import { Fragment } from 'react';

import { HeaderButton } from '@/components/headerItems/HeaderButton';
import { HeaderMenuButton } from '@/components/headerItems/HeaderMenuButton';
import type { HeaderItem, HeaderItemTone } from '@/components/headerItems/types';
import { HStack } from '@/components/ui/hstack';
import { View } from '@/components/ui/view';

type HeaderItemRowProps = {
  items: HeaderItem[];
  tone?: HeaderItemTone;
};

function HeaderItemView({ item, tone }: { item: HeaderItem; tone: HeaderItemTone }) {
  if (item.type === 'view') {
    return <>{item.render()}</>;
  }

  if (item.type === 'menu') {
    return <HeaderMenuButton item={item} tone={tone} />;
  }

  return <HeaderButton item={item} tone={tone} />;
}

/**
 * A header slot's items drawn as views, for Android's top app bar and the web header row.
 *
 * Renders nothing at all when every item is hidden, so an empty slot takes no width from
 * the title.
 *
 * A button is a 48px target with its own space around the glyph, so buttons sit flush. A
 * custom view brings no such padding, so one that follows another item is set apart from it.
 */
export function HeaderItemRow({ items, tone = 'default' }: HeaderItemRowProps) {
  const visibleItems = items.filter((item) => !item.isHidden);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <HStack className="items-center">
      {visibleItems.map((item, index) => (
        <View key={item.key} className={item.type === 'view' && index > 0 ? 'ml-4' : undefined}>
          <HeaderItemView item={item} tone={tone} />
        </View>
      ))}
    </HStack>
  );
}
